import React, { useState, useRef } from "react";
import { Row, Col, Form, Button, Spinner } from "react-bootstrap";
import { FiUser, FiMail, FiPhone, FiMapPin, FiSave, FiEdit2, FiCamera } from "react-icons/fi";
import axios from "axios";

// Separate Header Component
const CustomerHeader = ({ customer, customerId, onCustomerUpdate, onShowSuccess, onShowError }) => {
    const [uploadingImage, setUploadingImage] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);
    const fileInputRef = useRef(null);

    const handleImageChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                onShowError("Please select an image file");
                return;
            }
            
            if (file.size > 2 * 1024 * 1024) {
                onShowError("Image size should be less than 2MB");
                return;
            }

            setImagePreview(URL.createObjectURL(file));
            await uploadImage(file);
        }
    };

    const uploadImage = async (file) => {
        try {
            setUploadingImage(true);
            
            const AuthToken = localStorage.getItem("authToken");
            const formDataToSend = new FormData();
            formDataToSend.append('img', file);
            formDataToSend.append('_method', 'PUT');

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/customers-update/${customerId}`,
                formDataToSend,
                { 
                    headers: { 
                        Authorization: `Bearer ${AuthToken}`,
                        'Content-Type': 'multipart/form-data'
                    } 
                }
            );

            if (response.data.success) {
                onShowSuccess("Profile image updated successfully");
                onCustomerUpdate();
            } else {
                onShowError(response.data.message || "Failed to upload image");
            }
        } catch (err) {
            console.error("Error uploading image:", err);
            onShowError(err.response?.data?.message || "Failed to upload image");
        } finally {
            setUploadingImage(false);
            setImagePreview(null);
        }
    };

    const getImageUrl = (imgPath) => {
        if (!imgPath) return null;
        if (imgPath.startsWith('http')) return imgPath;
        return `${process.env.REACT_APP_IMG_URL}${imgPath}`;
    };

    return (
        <Row className="mb-4">
            <Col md={12}>
                <div className="d-flex align-items-center">
                    <div className="position-relative">
                        <div
                            className="d-flex align-items-center justify-content-center"
                            style={{
                                width: '100px',
                                height: '100px',
                                borderRadius: '50%',
                                background: '#e2e8f0',
                                overflow: 'hidden',
                                position: 'relative'
                            }}
                        >
                            {imagePreview ? (
                                <img
                                    src={imagePreview}
                                    alt="Preview"
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover'
                                    }}
                                />
                            ) : customer.img ? (
                                <img
                                    src={getImageUrl(customer.img)}
                                    alt={customer.name}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover'
                                    }}
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.style.display = 'none';
                                        e.target.parentElement.innerHTML = '<svg stroke="currentColor" fill="currentColor" stroke-width="0" viewBox="0 0 24 24" size="50" color="#4a5568" style="color: #4a5568;" height="50" width="50" xmlns="http://www.w3.org/2000/svg"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"></path></svg>';
                                    }}
                                />
                            ) : (
                                <FiUser size={50} color="#4a5568" />
                            )}
                        </div>
                        
                        <div
                            onClick={() => fileInputRef.current?.click()}
                            style={{
                                position: 'absolute',
                                bottom: '0',
                                right: '0',
                                backgroundColor: '#004d4d',
                                borderRadius: '50%',
                                padding: '8px',
                                cursor: 'pointer',
                                border: '2px solid white',
                                transition: 'all 0.3s ease'
                            }}
                        >
                            {uploadingImage ? (
                                <Spinner 
                                    as="span" 
                                    animation="border" 
                                    size="sm" 
                                    style={{ width: '16px', height: '12px', color: 'white' }}
                                />
                            ) : (
                                <FiCamera size={16} color="white" />
                            )}
                        </div>
                    </div>
                    
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        style={{ display: 'none' }}
                    />
                    
                    <div className="ms-3">
                        <h2 className="mb-1">{customer.name}</h2>
                    </div>
                </div>
            </Col>
        </Row>
    );
};

// Main CustomerInfoTab Component
const CustomerInfoTab = ({ customer, customerId, onCustomerUpdate, onShowSuccess, onShowError }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    
    const [formData, setFormData] = useState({
        name: customer?.name || '',
        email: customer?.email || '',
        phone: customer?.phone || '',
        address: customer?.address || '',
    });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleUpdate = async () => {
        try {
            setSaving(true);
            
            const AuthToken = localStorage.getItem("authToken");
            const formDataToSend = new FormData();
            formDataToSend.append('name', formData.name);
            formDataToSend.append('email', formData.email);
            formDataToSend.append('phone', formData.phone);
            formDataToSend.append('address', formData.address);
            formDataToSend.append('_method', 'PUT');

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/customers-update/${customerId}`,
                formDataToSend,
                { 
                    headers: { 
                        Authorization: `Bearer ${AuthToken}`,
                        'Content-Type': 'multipart/form-data'
                    } 
                }
            );

            if (response.data.success) {
                onShowSuccess("Customer updated successfully");
                setIsEditing(false);
                onCustomerUpdate();
            } else {
                onShowError(response.data.message || "Failed to update customer");
            }
        } catch (err) {
            console.error("Error updating customer:", err);
            if (err.response?.data?.errors) {
                const errors = err.response.data.errors;
                const errorMessages = Object.values(errors).flat().join(', ');
                onShowError(errorMessages);
            } else {
                onShowError(err.response?.data?.message || "Failed to update customer");
            }
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setIsEditing(false);
        setFormData({
            name: customer?.name || '',
            email: customer?.email || '',
            phone: customer?.phone || '',
            address: customer?.address || '',
        });
    };

    return (
        <>
            <CustomerHeader 
                customer={customer}
                customerId={customerId}
                onCustomerUpdate={onCustomerUpdate}
                onShowSuccess={onShowSuccess}
                onShowError={onShowError}
            />

            <div className="mt-4">
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">
                                <FiUser className="me-2" /> Full Name <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                placeholder="Enter full name"
                                disabled={!isEditing}
                                className={!isEditing ? "bg-light" : ""}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">
                                <FiMail className="me-2" /> Email Address <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                placeholder="Enter email address"
                                disabled={!isEditing}
                                className={!isEditing ? "bg-light" : ""}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">
                                <FiPhone className="me-2" /> Phone Number <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                type="tel"
                                name="phone"
                                value={formData.phone}
                                onChange={handleInputChange}
                                placeholder="Enter phone number"
                                disabled={!isEditing}
                                className={!isEditing ? "bg-light" : ""}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">
                                <FiMapPin className="me-2" /> Address
                            </Form.Label>
                            <Form.Control
                                type="text"
                                name="address"
                                value={formData.address}
                                onChange={handleInputChange}
                                placeholder="Enter address"
                                disabled={!isEditing}
                                className={!isEditing ? "bg-light" : ""}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                {!isEditing ? (
                    <div className="mt-4 text-end">
                        <Button 
                            variant="primary" 
                            onClick={() => setIsEditing(true)}
                            className="px-4"
                        >
                            <FiEdit2 className="me-2" /> Edit Profile
                        </Button>
                    </div>
                ) : (
                    <div className="mt-4 d-flex gap-2 justify-content-end">
                        <Button 
                            variant="secondary" 
                            onClick={handleCancel}
                            disabled={saving}
                        >
                            Cancel
                        </Button>
                        <Button 
                            variant="success" 
                            onClick={handleUpdate}
                            disabled={saving}
                            className="px-4"
                        >
                            <FiSave className="me-2" />
                            {saving ? 'Saving...' : 'Update Customer'}
                        </Button>
                    </div>
                )}
            </div>
        </>
    );
};

export default CustomerInfoTab;